import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injectable,
  OnDestroy,
  OnInit,
  QueryList,
  Type,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, Observable, of, Subscription, takeWhile } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { objectValue, safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { FormValueService } from 'src/app/common/guards/save-data-guard/form-value.service';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { FormConstructorProfileBaseComponent } from 'src/app/forms/form-constructors/form-constructor-profile-base/form-constructor-profile-base.component';
import { FormConstructorComponent } from 'src/app/forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from 'src/app/forms/form-state.service';
import { ProfileFormTypes } from 'src/app/forms/forms.enum';
import { Session } from 'src/app/interfaces';
import { FullUser } from 'src/app/swagger-api';
import { AttributeValue, FormConstructorContent } from '../../../../interfaces/form-and-field.interface';
import {
  EditProfileChangesContent,
  ManageRolesContent,
  ProfileFormValue,
} from '../../../../interfaces/profile.interface';
import { NotificationContextAttributesService } from '../../../../notifications/notifications-context-attributes.service';
import { NotificationService } from '../../../../notifications/notifications.service';
import { EditProfileParts, UserTypes } from '../../profile.enum';
import { ProfileService } from '../../profile.service';
import { EditProfileService } from './edit-profile.service';
import { FormContentService } from './form-content.service';
import { IndividualUserComponent } from './user-type/individual-user/individual-user.component';
import { OrganizationComponent } from './user-type/organization/organization.component';
import { UserTypeFormDirective } from './user-type/user-type-form-directive/user-type-form.directive';
import { UserTypeState } from './user-type/user-type.state';

//  TODO: Get rid of using session servise as the store for an user
//  EditProfileComponent, EditProfileService, FormContentService

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  providers: [EditProfileService, FormContentService],
})
@Injectable()
export class EditProfileComponent implements OnInit, OnDestroy, IDeactivate {
  public readonly LINK_PROFILE_VIEW: string = ProjectLinks.LINK_PROFILE_VIEW;
  public readonly LINK_CHANGE_PASSWORD: string = ProjectLinks.LINK_CHANGE_PASSWORD;

  public isLoading$: Observable<boolean>;
  public baseFormContent: FormConstructorContent;
  public copyBaseFormContent: AttributeValue[] | object;
  public errorForm = false;
  public isFormChanged: boolean;
  public formsChangeStatus: {
    baseForm: boolean;
    secondaryForms: Map<string, boolean>;
  } = {
    baseForm: false,
    secondaryForms: new Map(),
  };

  @ViewChild(UserTypeFormDirective, { static: true }) public userTypeFormDirective: UserTypeFormDirective;
  @ViewChild(FormConstructorProfileBaseComponent, { static: false })
  private baseForm: FormConstructorProfileBaseComponent;

  private readonly profileChangesTypes: {
    [key in EditProfileParts]: (content?: EditProfileChangesContent) => void;
  } = {
    userType: (content: EditProfileChangesContent) => {
      let array: FormConstructorContent[] = this.getFormsContent();
      array = (content.data as FormConstructorContent[]).concat(array);
      this.setFormsContent(array);
    },
    role: (content: EditProfileChangesContent) => {
      this.setRoleFormContent(content);
    },
    roles: (content: EditProfileChangesContent) => {
      this.setRolesFormContent(content);
    },
    on_removed_role: (content: EditProfileChangesContent) => {
      this.removeRoleFormContent(content);
    },
    on_removed_roles: (content: EditProfileChangesContent) => {
      this.setIndividualFormsContent(content.data as FormConstructorContent[]);
    },
  };

  private readonly addComponent: { [key: string]: () => void } = {
    individual: () => this.addIndividualUserComponent(),
    organization: () => this.addOrganizationComponent(),
  };

  private readonly componentTypes: { [key: string]: Type<UserTypeState> } = {
    individual: IndividualUserComponent,
    organization: OrganizationComponent,
  };

  private userTypeComponent: ComponentRef<UserTypeState>;
  private subscriptions$: Subscription[] = [];
  private selectedUserType: string = UserTypes.NONE;

  private profileService = inject(ProfileService);
  private formContentService = inject(FormContentService);
  private sessionService = inject(SessionService);
  private editProfileService = inject(EditProfileService);
  private formStateService = inject(FormStateService);
  private notificationService = inject(NotificationService);
  private notificationContextService = inject(NotificationContextAttributesService);
  private operationInfoService = inject(OperationInfoService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private formValueService = inject(FormValueService);

  public ngOnInit(): void {
    this.subscribeOnProfileUserInfoChanges();
    this.initUserInfo();

    this.isLoading$ = this.profileService.isLoading$;
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onFormComponentInitiated(form: UntypedFormGroup): void {
    this.subscribeToBaseFormContentChanges(form);
  }

  public deleteProfile(): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'delete-profile-btn' },
        successfulMessage: 'REMOVED_ACCOUNT_DIALOG_DESCRIPTION_SUCCESS',
        confirmationDialog: {
          title: 'REMOVE_ACCOUNT_DIALOG_TITLE',
          description: 'REMOVE_ACCOUNT_DIALOG_DESCRIPTION',
          buttons: this.notificationContextService.getDeleteProfileButtons(),
        },
      },
      () => {
        this.editProfileService.deleteProfile(this.sessionService.userId);
      },
    );
  }

  public saveProfileInfo(event: Event): void {
    this.formStateService.stopAllAction(event);
    if (this.userTypeComponent) {
      this.userTypeComponent.instance.saveUserTypeChanges();
    }
    this.errorForm =
      this.userTypeComponent.instance.someInvalidForms.some((isValid: boolean) => isValid === false) ||
      this.baseForm.childForm.form.invalid;

    if (this.errorForm) {
      return;
    }

    if (
      !this.sessionService.isAdminSession() &&
      this.userTypeComponent.instance['userRolesInfo'] &&
      (this.userTypeComponent.instance['userRolesInfo'] as []).length === 0
    ) {
      this.notificationService.dialogDefault({
        title: 'PROFILE_UNABLE_CHANGES_SAVE_DIALOG_TITLE',
        description: 'PROFILE_UNABLE_CHANGES_SAVE_DESCRIPTION',
        buttons: this.notificationContextService.getDefaultCloseAttr(),
      });

      return;
    }
    this.isFormChanged = false;
    this.formValueService.saveFormValue(this.baseForm.childForm.form.value);
  }

  private initUserInfo(): void {
    // Normally we mustn't be on this page without user
    // it's workaround when we reload the whole edit profile page
    const user$: Observable<FullUser> = this.sessionService.user
      ? of(this.sessionService.user)
      : this.sessionService.sessionChanges.pipe(map((session: Session) => session.user));

    this.subscriptions$.push(
      user$.pipe(takeWhile((user: FullUser) => Boolean(user))).subscribe((user: FullUser) => {
        if (user) {
          this.profileService.setupUserInfo(user.user_id);
        }
      }),
    );
  }

  private removeRoleFormContent(content: EditProfileChangesContent): void {
    let array: FormConstructorContent[] = this.getFormsContent();

    const roleType = (content.data as FormConstructorContent).formAttribute.type;

    array = array.filter((item: FormConstructorContent) => item.formAttribute.type !== roleType);

    this.setIndividualFormsContent(array);
  }

  private setRoleFormContent(content: EditProfileChangesContent): void {
    const array: FormConstructorContent[] = this.getFormsContent();
    array.push(content.data as FormConstructorContent);
    this.setIndividualFormsContent(array);
  }

  private setRolesFormContent(content: EditProfileChangesContent): void {
    let array: FormConstructorContent[] = this.getFormsContent();
    array = array.concat(content.data as FormConstructorContent[]);
    this.setIndividualFormsContent(array);
  }

  private getFormsContent(): FormConstructorContent[] {
    return this.userTypeComponent.instance.formsContent;
  }

  private setFormsContent(array: FormConstructorContent[]): void {
    this.userTypeComponent.instance.formsContent = array;

    if (this.sessionService.hasNoUserType || this.sessionService.isIncompleteIndividual()) {
      this.scrollToBaseFormBottom();
    }
  }

  /**
   * @description Scrolls page content to bottom of base form.
   *              Called only if the user has no type.
   */
  private scrollToBaseFormBottom(): void {
    setTimeout(() => {
      window.scrollTo(0, document.getElementById(`${this.baseFormContent.formAttribute.dataTestId}`).scrollHeight);
    });
  }

  private setIndividualFormsContent(array: FormConstructorContent[]): void {
    this.setFormsContent(array);
    (this.userTypeComponent.instance as IndividualUserComponent).userRolesInfo = this.profileService.userRolesInfo;
  }

  private initEditProfileComponent(): void {
    this.baseFormContent = this.formContentService.getBaseFormContent();
    if (!this.copyBaseFormContent) {
      this.copyBaseFormContent = this.baseFormContent.data;
    }
    this.subscribeOnProfileChanges();
    this.addUserTypeComponent();
  }

  private subscribeOnProfileUserInfoChanges(): void {
    this.subscriptions$.push(
      this.profileService.metadataChanges$.subscribe((result: boolean) => {
        if (result) {
          this.initEditProfileComponent();
        }
      }),
    );
  }

  private addUserTypeComponent(): void {
    if (!this.sessionService.hasNoUserType) {
      this.initUserTypeComponent(this.sessionService.userType);
    }
  }

  private initUserTypeComponent(userType: string): void {
    this.userTypeComponent = this.userTypeFormDirective.container.createComponent(this.componentTypes[userType]);

    this.selectedUserType = userType;
    this.addComponent[userType]();

    this.userTypeComponent.instance.isAdminSession = this.sessionService.isAdminSession();
    this.subscriptions$.push(
      this.userTypeComponent.instance.userComponentInitiated.subscribe((data) => {
        this.subscribeToUserTypeFormsContentChanges(data);
      }),
    );
  }

  private addOrganizationComponent(): void {
    this.formContentService.getOrganizationFormsContent();
    this.saveUserTypeData();
  }

  private addIndividualUserComponent(): void {
    this.formContentService.getIndividualFormsContent();
    this.saveIndividualComponentData();
  }

  private saveUserTypeData(): void {
    this.userTypeComponent.instance.saveUserType.subscribe((formsValue: ProfileFormValue[]) => {
      if (this.baseForm.childForm.form.valid) {
        this.baseForm.prepareSubmitData();
        formsValue.unshift({
          formType: ProfileFormTypes.BASE,
          formValue: this.baseForm.formContent.data,
        });

        this.formsChangeStatus.secondaryForms.clear();

        this.operationInfoService.setupOperation(
          {
            spinner: 'global',
            button: { id: 'btn-save-edit-profile' },
            successfulMessage: this.getModalDescription(formsValue),
          },
          () => {
            this.editProfileService.updateUserInfo(formsValue, this.selectedUserType);
          },
        );
      }
    });
  }

  private getModalDescription(formsValue: ProfileFormValue[]): string {
    const baseForm: ProfileFormValue = formsValue.find(
      (item: ProfileFormValue) => item.formType === ProfileFormTypes.BASE,
    );
    const newEmail = baseForm.formValue['verified_email'] as string;
    const oldEmail = this.copyBaseFormContent['verified_email'] as string;
    const hasEmailChanged: boolean = oldEmail && oldEmail !== newEmail;
    const textMailUpdate: string = this.translateService.instant(
      'PROFILE_PROFILE_SUCCESSFUL_UPDATE_DESCRIPTION_MAIL_HINT',
      {
        newMail: newEmail,
        oldMail: oldEmail,
      },
    ) as string;

    if (hasEmailChanged) {
      return textMailUpdate;
    } else {
      return 'PROFILE_PROFILE_SUCCESSFUL_UPDATE_DESCRIPTION_HINT';
    }
  }
  private saveIndividualComponentData(): void {
    this.saveUserTypeData();
    this.subscribeOnRoleChanges();
  }

  private subscribeOnProfileChanges(): void {
    this.subscriptions$.push(
      this.formContentService.changes.subscribe((changesContent: EditProfileChangesContent) => {
        const contentChanges: (content?: EditProfileChangesContent) => void = objectValue(
          this.profileChangesTypes,
          <EditProfileParts>changesContent.formType,
        );

        contentChanges(changesContent);
      }),
    );
  }

  private subscribeOnRoleChanges(): void {
    (this.userTypeComponent.instance as IndividualUserComponent).manageRoles.subscribe(
      (content: ManageRolesContent) => {
        this.editProfileService.manageRoles(content);
        this.isFormChanged = true;
      },
    );
  }

  private subscribeToBaseFormContentChanges(form: UntypedFormGroup): void {
    this.formValueService.saveFormValue(form.value);
    this.subscriptions$.push(
      combineLatest([form.valueChanges, this.formValueService.valueChanged]).subscribe(
        ([value, startValue]: [object, object]) => {
          this.formsChangeStatus.baseForm = this.formValueService.formChangeDetection(value, startValue);
          this.isFormChanged = this.formsChangeStatus.baseForm || this.isAnySecondaryFormModified();
          safelyDetectChanges(this.cdr);
        },
      ),
    );
  }

  private subscribeToUserTypeFormsContentChanges(userTypeForms: QueryList<FormConstructorComponent>): void {
    userTypeForms.forEach((item: FormConstructorComponent) => {
      this.subscribeToFormChanges(item.childForm.form, item.formContent.formAttribute.dataTestId);
    });
  }

  private subscribeToFormChanges(form: UntypedFormGroup, formUniqueId: string): void {
    const currentForm = structuredClone(form.value as object);
    this.subscriptions$.push(
      form.valueChanges.subscribe((value: object) => {
        this.formsChangeStatus.secondaryForms.set(
          formUniqueId,
          this.formValueService.formChangeDetection(value, currentForm),
        );

        this.isFormChanged = this.formsChangeStatus.baseForm || this.isAnySecondaryFormModified();
        safelyDetectChanges(this.cdr);
      }),
    );
  }

  private isAnySecondaryFormModified(): boolean {
    for (const [_formId, isFormChanged] of this.formsChangeStatus.secondaryForms) {
      if (isFormChanged) {
        return true;
      }
    }
    return false;
  }
}
